import { DetailItem } from './TradeDetailPaymentLazada';
import { useTranslate } from '@tolgee/react';
import operateBtns from 'components/Refund/OperateBtns';
import { useReturnOperationConfig } from './hooks/useReturnOperationConfig';
import { ReverseLogistics } from './ReverseLogistics';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { Tooltip } from 'antd';
import { useReturnItemDisplayState } from 'views/Refund/components/ReturnListItem/hooks/useReturnItemDisplayState';
import { toUpperCase } from 'utils/toLowerCase';

export interface TradeReturnStatusProps {
  sn: string;
  conversation: Conversation.Conversation;
  trade: Conversation.Trade.TradeWithLogistics;
  refundReason: Conversation.Trade.RefundReason;
  data: Conversation.Trade.ReturnProduct[];
  exportMode: boolean;
}

/** 展示：最新状态文案 */
function StatusItemView({ displayStatusText }: { displayStatusText: string | null }) {
  const { t } = useTranslate();
  if (!displayStatusText) return null;
  return (
    <DetailItem>
      <span>{t('scene.chatWorkspace.orderTab.returnRefund.label.latestStatus')}</span>
      <span className="text-[14px] text-[#232426]">{displayStatusText}</span>
    </DetailItem>
  );
}

/** 展示：截止时间提示 */
function TimeTipItemView({ timeTip }: { timeTip: string | null }) {
  if (!timeTip) return null;
  return (
    <DetailItem>
      <span />
      <span className="text-[#F05B5B]">{timeTip}</span>
    </DetailItem>
  );
}

/** 展示：操作按钮区，仅负责渲染 operateBtns 产出的按钮 */
function OperationButtonsView({
  operationConfig,
  disabled,
}: {
  operationConfig: Parameters<typeof operateBtns>[0];
  disabled?: boolean;
}) {
  const optBtns = operateBtns({
    ...operationConfig,
    disabled,
  } as any);
  return <div className="flex items-center justify-end flex-wrap gap-2 py-2">{optBtns}</div>;
}

/** 容器：编排状态、时间提示、操作按钮，业务逻辑由 hooks 提供 */
export function TradeReturnStatus(props: TradeReturnStatusProps) {
  const { sn, conversation, trade, refundReason, data, exportMode } = props;
  const { channel, region, storeId } = conversation;

  const { t } = useTranslate();

  const { operationConfig } = useReturnOperationConfig(sn, conversation, trade, data, refundReason);

  const permission = useGetPermission()('操作弃单', {
    platform: channel,
    region,
    storeId: Number(storeId),
  });

  const { returnStatusTip, shopeeTimeTip, tkTimeTip } = useReturnItemDisplayState({
    row: {
      ...refundReason,
      returnShipDueDateMs: '',
      returnUpdateTime: '',
    },
    platform: toUpperCase(trade.platform),
    operatable: permission,
  });

  return (
    <div className="flex flex-col w-full border-r-0 border-b-0 border-l-0 border-t border-[rgba(0,0,0,0.05)] border-solid pt-[8px]">
      <StatusItemView displayStatusText={returnStatusTip} />
      <TimeTipItemView timeTip={shopeeTimeTip?.replyDisplay || tkTimeTip?.text || null} />
      <ReverseLogistics {...props} />
      {!exportMode &&
        (permission ? (
          <OperationButtonsView operationConfig={operationConfig} />
        ) : (
          <Tooltip
            title={t('scene.chatWorkspace.orderTab.returnRefund.tooltip.noAfterSalesPermission')}
          >
            <div>
              <OperationButtonsView disabled operationConfig={operationConfig} />
            </div>
          </Tooltip>
        ))}
    </div>
  );
}
